/* import __COLOCATED_TEMPLATE__ from './fin-readiness-table.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';

import type Store from '@ember-data/store';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type IntlService from 'embercom/services/intl';

interface Args {
  reportState: ReportState;
  teammateOnlyConversationCount: number;
}

const TEAMMATE_COST_PER_HOUR = 23;
const AVERAGE_TIME_TO_HANDLE_CONVERSATION = 35;
const FIN_RESOLUTION_COST = 0.99;

export default class FinReadinessTable extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;

  @use counterResource = staticDataResource(this, {
    serializedChart: () => this.informationalConversationsChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use convWithQueryTypeResource = staticDataResource(this, {
    serializedChart: () => this.convWithQueryTypeChartDefinition,
    reportState: () => this.args.reportState,
  });

  get informationalPersonalizedActionsConversationsFilter() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
        values: ['informational', 'personalized', 'actions'],
      },
    };
  }

  get queryTypeFilter() {
    return {
      type: 'exists',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
      },
    };
  }

  get informationalConversationsChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.informationalPersonalizedActionsConversationsFilter],
          },
        },
      ],
    };
  }

  get convWithQueryTypeChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.queryTypeFilter],
          },
        },
      ],
    };
  }

  get informationalConversationsValue() {
    return this.counterResource.formatValue({
      value: this.counterResource.kpiData[0],
      metricId: this.informationalConversationsChartDefinition.chart_series[0].metric_id,
    });
  }

  get convWithQueryTypeValue() {
    return this.convWithQueryTypeResource.formatValue({
      value: this.convWithQueryTypeResource.kpiData[0],
      metricId: this.convWithQueryTypeChartDefinition.chart_series[0].metric_id,
    });
  }

  get informationalPercentage() {
    let informationalConversationsValue = this.counterResource.kpiData[0] as number;
    let convValue = this.convWithQueryTypeResource.kpiData[0] as number;
    return `${((informationalConversationsValue / convValue) * 100).toFixed(0)}%`;
  }

  get averageTime() {
    return AVERAGE_TIME_TO_HANDLE_CONVERSATION;
  }

  get teammateCost() {
    return this.intl.formatNumber(TEAMMATE_COST_PER_HOUR, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }

  get amountSavedUsingFin() {
    let teammateCost =
      this.args.teammateOnlyConversationCount *
      (AVERAGE_TIME_TO_HANDLE_CONVERSATION / 60) *
      TEAMMATE_COST_PER_HOUR;
    let finResolutionCost = this.args.teammateOnlyConversationCount * FIN_RESOLUTION_COST;
    return this.intl.formatNumber(teammateCost - finResolutionCost, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }

  get isLoading() {
    return this.counterResource.isLoading || this.convWithQueryTypeResource.isLoading;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessTable': typeof FinReadinessTable;
  }
}
